import React from 'react';

import {
  AdSlot,
  ContainerId,
  debugLog,
  type LayoutType,
  type PlacementId
} from '@schibsted-nmp/advertising-shared';

import { $placementsMap, getPlacementList } from '../atoms/placements.js';
import { shouldCollapse } from '../utils/index.js';
import { injectSlotInWebComponent } from '../utils/injectSlot.js';

/**
 * Allows to inject a slot into a web component. This is used to get an element
 * from the Light DOM drilling into the Shadow DOM for 1 level deep.
 */
export function AdvertisingSlotComponent(
  props: AdvertisingSlotComponent.Props
) {
  return <AdvertisingSlotComponent.Forwarded {...props} />;
}

export namespace AdvertisingSlotComponent {
  export type Props = React.ComponentProps<typeof Forwarded>;

  export const Forwarded = React.forwardRef<HTMLSlotElement, Forwarded.Props>(
    ({ itemId, categories, initialLayoutType, webComponentName }, ref) => {
      const placementData = PlacementData.use(itemId);

      useHideParentElementOnAdLoadFailure(placementData, webComponentName);

      useInjectSlotInWebComponent(
        placementData,
        categories,
        initialLayoutType,
        webComponentName
      );

      return placementData && <slot ref={ref} name={placementData.slotId} />;
    }
  );

  export namespace Forwarded {
    export type Props = {
      /** The id of the item to get the `placementId` for. */
      itemId: PlacementId;

      /** The name of the web component to inject the slot into. */
      webComponentName: string;

      /** The categories of the item to get the `placementId` for. Stringified JSON object. */
      categories?: string | undefined;

      /** The initial layout type of the item to get the `placementId` for. (Grid or List) */
      initialLayoutType?: LayoutType | undefined;
    };
  }

  export type PlacementData = ReturnType<typeof PlacementData.use>;

  export namespace PlacementData {
    export function use(itemId: PlacementId) {
      return React.useMemo(() => {
        let adIndex = -1;

        const placement = getPlacementList().find(({ placementId }, index) => {
          if (placementId === itemId) {
            adIndex = index;
            return true;
          }

          return false;
        });

        if (!placement) {
          debugLog(`Placement not in placementMap for placementId: ${itemId}`);
          return null;
        }

        const { placementId } = placement;

        return {
          placementId,
          adIndex,
          containerId: ContainerId.fromPlacementId(placementId),
          slotId: AdSlot.fromPlacementId(placementId)
        } as const;
      }, [itemId]);
    }
  }

  export function useHideParentElementOnAdLoadFailure(
    placementData: PlacementData,
    webComponentName: string
  ) {
    // Hide the parent element if the ad fails to load
    React.useEffect(() => {
      if (!placementData) return () => void 0;

      const { placementId } = placementData;

      return $placementsMap.subscribe((placements) => {
        const placement = placements[placementId];

        if (!placement) return;
        if (
          !['error', 'collapsedOnCollision', 'loaded'].includes(
            placement.status
          )
        )
          return;

        const parentElement =
          typeof document !== 'undefined'
            ? document
                .querySelector(webComponentName)
                ?.shadowRoot?.getElementById(placementId)
            : null;

        if (!parentElement) return;

        // Makes sure the ad does not take up any space in the grid
        // The children will be empty so it won't take up height etc.
        parentElement.style.gridColumn = '1 / -1';
        if (shouldCollapse(placement)) {
          parentElement.classList.add('advt-ad-collapsed');
          parentElement.style.display = 'none';
        }
      });
    }, [placementData, webComponentName]);
  }

  export function useInjectSlotInWebComponent(
    placementData: PlacementData,
    categories: string | undefined,
    initialLayoutType: LayoutType | undefined,
    webComponentName: string
  ) {
    const [slotAdded, setSlotAdded] = React.useState<boolean>(false);

    React.useEffect(() => {
      if (slotAdded) return;
      if (!placementData) return;

      injectSlotInWebComponent({
        ...placementData,
        categories,
        initialLayoutType,
        webComponentName
      });

      setSlotAdded(true);
    }, [
      slotAdded,
      placementData,
      categories,
      initialLayoutType,
      webComponentName
    ]);
  }
}
